import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Bookmark } from 'lucide-react';
import { db, auth } from '../firebase';
import { collection, query, orderBy, getDocs, limit, startAfter, doc, getDoc } from 'firebase/firestore';

export const SavedPostsPage = () => {
  const navigate = useNavigate();
  
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const fetchSavedPosts = async (isLoadMore = false) => {
    if (isLoadMore && (!hasMore || fetchingMore)) return;

    if (isLoadMore) setFetchingMore(true);
    else setLoading(true);

    try {
        const uid = auth.currentUser?.uid;
        if (!uid) {
            navigate('/login');
            return;
        }

        let q = query(
            collection(db, 'users', uid, 'savedPosts'),
            orderBy('savedAt', 'desc'),
            limit(20)
        );

        if (isLoadMore && lastDoc) {
            q = query(
                collection(db, 'users', uid, 'savedPosts'),
                orderBy('savedAt', 'desc'),
                startAfter(lastDoc),
                limit(20)
            );
        }

        const snapshot = await getDocs(q);
        
        if (snapshot.empty) {
            setHasMore(false);
            if (!isLoadMore) setLoading(false);
            else setFetchingMore(false);
            return;
        }

        setLastDoc(snapshot.docs[snapshot.docs.length - 1]);

        const savedItems = snapshot.docs
            .map(d => {
                const data = d.data();
                return data && data.postId ? { savedId: d.id, postId: data.postId } : null;
            })
            .filter(Boolean);

        const postPromises = savedItems.map(item => getDoc(doc(db, 'sparkposts', item.postId)));
        const postSnapshots = await Promise.all(postPromises);

        const validPosts = postSnapshots
            .filter(p => p.exists())
            .map(p => ({ id: p.id, ...p.data() }));

        if (isLoadMore) {
            setPosts(prev => {
                const existingIds = new Set(prev.map(p => p.id));
                const newPosts = validPosts.filter(p => !existingIds.has(p.id));
                return [...prev, ...newPosts];
            });
        } else {
            setPosts(validPosts);
        }

        if (snapshot.docs.length < 20) {
            setHasMore(false);
        }

    } catch (error) {
        console.error("Error fetching saved posts:", error);
    } finally {
        if (isLoadMore) setFetchingMore(false);
        else setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
        if (user) {
            fetchSavedPosts();
        } else {
            navigate('/login');
        }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 500) {
        fetchSavedPosts(true);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore, fetchingMore, lastDoc]);

  return (
    <main className="w-full sm:w-[700px] min-h-screen border-r border-gray-800 flex flex-col shrink-0 bg-black text-white font-poppins selection:bg-[#0BD9E5]/30 pb-20 sm:pb-0">
      
      {/* Header */}
      <div className="sticky top-0 bg-black/80 backdrop-blur-md z-40 border-b border-gray-800 flex items-center justify-between p-4">
        <div className="flex items-center gap-3">
           <button onClick={() => navigate('/')} className="p-2 hover:bg-white/10 rounded-full transition-colors sm:hidden">
             <ArrowLeft size={20} />
           </button>
           <h1 className="text-xl font-bold">Saved Posts</h1>
        </div>
      </div>

      <div className="flex-1 p-0.5">
        {loading ? (
            <div className="flex justify-center p-8">
                <Loader2 className="w-8 h-8 animate-spin text-[#0BD9E5]" />
            </div>
        ) : posts.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-center h-[50vh]">
                <div className="w-16 h-16 rounded-full bg-[#0BD9E5]/10 flex items-center justify-center mb-4">
                   <Bookmark className="w-8 h-8 text-[#0BD9E5]" />
                </div>
                <h2 className="text-xl font-bold mb-2">No Saved Posts</h2>
                <p className="text-gray-500 text-sm">When you save posts, they'll show up here.</p>
            </div>
        ) : (
            <div className="grid grid-cols-3 gap-0.5">
                {posts.map((post) => (
                    <div 
                        key={post.id} 
                        onClick={() => navigate(`/post/${post.id}`)}
                        className="aspect-square bg-gray-900 cursor-pointer overflow-hidden relative group hover:opacity-90 transition-opacity"
                    >
                        {post.imageUrl ? (
                            <img 
                                src={post.imageUrl} 
                                alt="" 
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full p-2 flex items-center justify-center bg-[#16181c]">
                                <p className="text-xs text-gray-300 line-clamp-6 break-words text-center">
                                    {post.caption}
                                </p>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        )}
        
        {fetchingMore && (
            <div className="flex justify-center p-6">
                <Loader2 className="w-6 h-6 animate-spin text-[#0BD9E5]" />
            </div>
        )}
      </div>
    </main>
  );
};

export default SavedPostsPage;
